import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { resources } from 'src/app/services/api';
import { environment } from 'src/environments/environment';
import {
	MOCK_COMPATIBILITY_RULES,
	MOCK_COMPATIBILITY_RULE_TYPES,
	MOCK_COMPONENTS,
	MOCK_HARDWARE,
	MOCK_PRODUCTS,
	MOCK_RELEASES,
	MOCK_RELEASE_NOTES,
	filterMockReleases,
} from '../conf/software-updates-mock.data';
/**
 * Client HTTP per Software Updates API.
 * Base path: {apiUrl}/updates/v1/management (management) e .../v1/client (endpoint client).
 *
 * Le chiamate usano sempre l'URL assoluto dell'ambiente corrente (environment.apiUrl),
 * così puntano direttamente al microservizio Software Updates dell'ambiente in uso.
 *
 * Le response seguono il pattern data-rest: { data, pagination?, code?, message? }.
 */
import {
	AddOrUpdateComponentRequest,
	AddOrUpdateHardwareRequest,
	CreateCompatibilityRuleRequest,
	UpdateCompatibilityRuleRequest,
	CreateOrUpdateProductRequest,
	CreateOrUpdateReleaseNoteRequest,
	CreateOrUpdateSoftwareReleaseRequest,
	PublishSoftwareReleaseRequest,
	ReleaseListQueryParams,
	UpdateFirmwareReleaseRequest,
} from 'src/app/conf/firmware-release';
import {
	CompatibilityRuleItemResponseData,
	CompatibilityRuleListResponseData,
	CompatibilityRuleTypesData,
	CompatibilityRuleTypesResponseData,
	ComponentItemResponseData,
	ComponentListResponseData,
	DownloadSoftwareReleaseItemResponseData,
	HardwareData,
	HardwareItemResponseData,
	HardwareListResponseData,
	ProductData,
	ProductItemResponseData,
	ProductResponseData,
	ReleaseNoteData,
	ReleaseNoteItemResponseData,
	ReleaseNoteListResponseData,
	SoftwareReleaseItemResponseData,
	SoftwareReleaseListResponseData,
	SoftwareUpdatesItemResponseData,
	SoftwareUpdatesListResponseData,
	SoftwareUpdatesVoidResponseData
} from 'src/app/model/api/data-rest';

// Abilita insieme ai rami mock (if (!environment.production)) per lavorare con dati mockati in locale.
const FAKE_DATA = false;

@Injectable({ providedIn: 'root' })
export class SoftwareUpdatesService {
	/** URL assoluto dell'ambiente corrente (environment.apiUrl include già /api). */
	private readonly apiBase = environment.apiUrl;
	private readonly managementBase = `${this.apiBase}${resources.get('updates')}${resources.get('v1')}/management`;
	private readonly clientBase = `${this.apiBase}${resources.get('updates')}${resources.get('v1')}/client`;

	constructor(private http: HttpClient) { }

	private useMockData(): boolean {
		return !environment.production && FAKE_DATA;
	}


	// --- Product ---
	/** Lista anagrafica prodotti (device, app, tool). */
	getProducts(filters?: { name?: string; types?: string[] }): Observable<ProductResponseData> {
		if (this.useMockData()) {
			const search = filters?.name?.trim().toLowerCase();
			const types = filters?.types ?? [];
			const data = MOCK_PRODUCTS
				.filter((p) => (types.length === 0 ? true : types.includes(p.type ?? '')))
				.filter((p) => (search ? (p.name?.toLowerCase().includes(search) ?? false) : true))
				.map((p) => ({ ...p, hardwares: MOCK_HARDWARE.filter((h) => h.productId === p.id) }));
			return of({ data });
		}
		let params = new HttpParams().set('hardware', 'true');
		const name = filters?.name?.trim();
		if (name) {
			params = params.set('name', name);
		}
		(filters?.types ?? []).forEach((type) => {
			params = params.append('types', type);
		});
		return this.http.get<ProductResponseData>(`${this.managementBase}/product`, { params }).pipe(
			map((body) => this.normalizeListResponse(body)),
		);
	}

	getProduct(productId: number): Observable<ProductItemResponseData> {
		if (this.useMockData()) {
			const product = MOCK_PRODUCTS.find((p) => p.id === productId);
			return of({ data: { ...product ?? {}, id: productId } });
		}
		return this.http.get<ProductItemResponseData | ProductItemResponseData['data']>(`${this.managementBase}/product/${productId}`).pipe(
			map((body) => this.normalizeItemResponse(body)),
		);
	}

	createProduct(body: CreateOrUpdateProductRequest): Observable<ProductItemResponseData> {
		if (this.useMockData()) {
			const nextId = Math.max(0, ...MOCK_PRODUCTS.map((p) => p.id ?? 0)) + 1;
			const created = { ...body, id: nextId } as ProductData;
			MOCK_PRODUCTS.push(created);
			return of({ data: created });
		}
		return this.http.post<ProductItemResponseData | ProductItemResponseData['data']>(`${this.managementBase}/product`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	updateProduct(productId: number, body: CreateOrUpdateProductRequest): Observable<ProductItemResponseData> {
		if (this.useMockData()) {
			const updated = { ...body, id: productId } as ProductData;
			const idx = MOCK_PRODUCTS.findIndex((p) => p.id === productId);
			if (idx !== -1) {
				MOCK_PRODUCTS[idx] = updated;
			}
			return of({ data: updated });
		}
		return this.http.put<ProductItemResponseData | ProductItemResponseData['data']>(`${this.managementBase}/product/${productId}`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	deleteProduct(productId: number): Observable<SoftwareUpdatesVoidResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_PRODUCTS.findIndex((p) => p.id === productId);
			if (idx !== -1) {
				MOCK_PRODUCTS.splice(idx, 1);
			}
			for (let i = MOCK_HARDWARE.length - 1; i >= 0; i--) {
				if (MOCK_HARDWARE[i].productId === productId) {
					MOCK_HARDWARE.splice(i, 1);
				}
			}
			return of({});
		}
		return this.http.delete<SoftwareUpdatesVoidResponseData>(`${this.managementBase}/product/${productId}`);
	}

	// --- Hardware ---
	getHardwareList(productId: number): Observable<HardwareListResponseData> {
		if (this.useMockData()) {
			return of({ data: MOCK_HARDWARE.filter((h) => h.productId === productId) });
		}
		return this.http.get<HardwareListResponseData | HardwareData[]>(`${this.managementBase}/product/${productId}/hardware`).pipe(
			map((body) => this.normalizeListResponse(body)),
		);
	}

	getHardware(hardwareId: number): Observable<HardwareItemResponseData> {
		if (this.useMockData()) {
			const hardware = MOCK_HARDWARE.find((h) => h.id === hardwareId);
			return of(hardware ? { data: hardware } : {});
		}
		return this.getProducts().pipe(
			map((response) => {
				for (const product of response?.data ?? []) {
					const hardware = product.hardwares?.find((h) => h.id === hardwareId);
					if (hardware) {
						return { data: hardware };
					}
				}
				return {};
			}),
		);
	}

	createHardware(productId: number, body: AddOrUpdateHardwareRequest): Observable<HardwareItemResponseData> {
		if (this.useMockData()) {
			const nextId = Math.max(0, ...MOCK_HARDWARE.map((h) => h.id ?? 0)) + 1;
			const created = { ...body, id: nextId, productId };
			MOCK_HARDWARE.push(created);
			return of({ data: created });
		}
		return this.http.post<HardwareItemResponseData | HardwareData>(`${this.managementBase}/product/${productId}/hardware`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	updateHardware(hardwareId: number, body: AddOrUpdateHardwareRequest): Observable<HardwareItemResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_HARDWARE.findIndex((h) => h.id === hardwareId);
			if (idx !== -1) {
				MOCK_HARDWARE[idx] = { ...MOCK_HARDWARE[idx], ...body, id: hardwareId };
			}
			return of({ data: { ...MOCK_HARDWARE[idx] } });
		}
		return this.http.put<HardwareItemResponseData | HardwareData>(`${this.managementBase}/product/hardware/${hardwareId}`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	deleteHardware(hardwareId: number): Observable<SoftwareUpdatesVoidResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_HARDWARE.findIndex((h) => h.id === hardwareId);
			if (idx !== -1) {
				MOCK_HARDWARE.splice(idx, 1);
			}
			return of({});
		}
		return this.http.delete<SoftwareUpdatesVoidResponseData>(`${this.managementBase}/product/hardware/${hardwareId}`);
	}

	// --- Component ---
	getComponents(hardwareId: number): Observable<ComponentListResponseData> {
		if (this.useMockData()) {
			return of({ data: MOCK_COMPONENTS.filter((c) => c.hardwareId === hardwareId) });
		}
		return this.http.get<ComponentListResponseData | ComponentListResponseData['data']>(`${this.managementBase}/hardware/${hardwareId}/component`).pipe(
			map((body) => this.normalizeListResponse(body)),
		);
	}

	createComponent(hardwareId: number, body: AddOrUpdateComponentRequest): Observable<ComponentItemResponseData> {
		if (this.useMockData()) {
			const nextId = Math.max(0, ...MOCK_COMPONENTS.map((c) => c.id ?? 0)) + 1;
			const created = { ...body, id: nextId, hardwareId };
			MOCK_COMPONENTS.push(created);
			return of({ data: created });
		}
		return this.http.post<ComponentItemResponseData | ComponentItemResponseData['data']>(`${this.managementBase}/hardware/${hardwareId}/component`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	updateComponent(componentId: number, body: AddOrUpdateComponentRequest): Observable<ComponentItemResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_COMPONENTS.findIndex((c) => c.id === componentId);
			if (idx !== -1) {
				MOCK_COMPONENTS[idx] = { ...MOCK_COMPONENTS[idx], ...body, id: componentId };
			}
			return of({ data: { ...MOCK_COMPONENTS[idx] } });
		}
		return this.http.put<ComponentItemResponseData | ComponentItemResponseData['data']>(`${this.managementBase}/hardware/component/${componentId}`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	deleteComponent(componentId: number): Observable<SoftwareUpdatesVoidResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_COMPONENTS.findIndex((c) => c.id === componentId);
			if (idx !== -1) {
				MOCK_COMPONENTS.splice(idx, 1);
			}
			return of({});
		}
		return this.http.delete<SoftwareUpdatesVoidResponseData>(`${this.managementBase}/hardware/component/${componentId}`);
	}

	// --- Release ---
	getReleasesByTarget(installationTargetId: number): Observable<SoftwareReleaseListResponseData> {
		return this.getReleases({ installationTargetId });
	}

	getReleases(params: ReleaseListQueryParams): Observable<SoftwareReleaseListResponseData> {
		if (this.useMockData()) {
			return of(filterMockReleases(params));
		}
		let httpParams = new HttpParams();
		if (params.offset != null) {
			httpParams = httpParams.set('offset', String(params.offset));
		}
		if (params.limit != null) {
			httpParams = httpParams.set('limit', String(params.limit));
		}
		if (params.installationTargetId != null) {
			httpParams = httpParams.set('installationTargetId', String(params.installationTargetId));
		}
		if (params.productId != null) {
			httpParams = httpParams.set('productId', String(params.productId));
		}
		if (params.hardwareId != null) {
			httpParams = httpParams.set('hardwareId', String(params.hardwareId));
		}
		if (params.componentId != null) {
			httpParams = httpParams.set('componentId', String(params.componentId));
		}
		const version = params.version?.trim();
		if (version) {
			httpParams = httpParams.set('version', version);
		}
		return this.http.get<SoftwareReleaseListResponseData | SoftwareReleaseListResponseData['data']>(`${this.managementBase}/release`, { params: httpParams }).pipe(
			map((body) => this.normalizeListResponse(body)),
		);
	}

	getRelease(softwareReleaseId: number): Observable<SoftwareReleaseItemResponseData> {
		if (this.useMockData()) {
			const release = MOCK_RELEASES.find((r) => r.id === softwareReleaseId);
			return of(release ? { data: release } : {});
		}
		return this.http.get<SoftwareReleaseItemResponseData | SoftwareReleaseItemResponseData['data']>(`${this.managementBase}/release/${softwareReleaseId}`).pipe(
			map((body) => this.normalizeItemResponse(body)),
		);
	}

	createRelease(body: CreateOrUpdateSoftwareReleaseRequest): Observable<SoftwareReleaseItemResponseData> {
		if (this.useMockData()) {
			const nextId = Math.max(0, ...MOCK_RELEASES.map((r) => r.id ?? 0)) + 1;
			const installationTargetId = body.target.componentId ?? body.target.hardwareId ?? body.target.productId;
			const created = {
				...body,
				id: nextId,
				version: body.version,
				installationTargetId,
				target: { ...body.target },
				isPublished: body.isPublished ?? false,
				byteSize: body.byteSize,
			};
			MOCK_RELEASES.push(created);
			return of({ data: created });
		}
		return this.http.post<SoftwareReleaseItemResponseData | SoftwareReleaseItemResponseData['data']>(`${this.managementBase}/release`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	updateRelease(softwareReleaseId: number, body: UpdateFirmwareReleaseRequest): Observable<SoftwareReleaseItemResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_RELEASES.findIndex((r) => r.id === softwareReleaseId);
			if (idx !== -1) {
				MOCK_RELEASES[idx] = { ...MOCK_RELEASES[idx], ...body, id: softwareReleaseId };
			}
			return of({ data: { ...MOCK_RELEASES[idx] } });
		}
		return this.http.put<SoftwareReleaseItemResponseData | SoftwareReleaseItemResponseData['data']>(`${this.managementBase}/release/${softwareReleaseId}`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	deleteRelease(softwareReleaseId: number): Observable<SoftwareUpdatesVoidResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_RELEASES.findIndex((r) => r.id === softwareReleaseId);
			if (idx !== -1) {
				MOCK_RELEASES.splice(idx, 1);
			}
			return of({});
		}
		return this.http.delete<SoftwareUpdatesVoidResponseData>(`${this.managementBase}/release/${softwareReleaseId}`);
	}

	publishRelease(softwareReleaseId: number, body: PublishSoftwareReleaseRequest): Observable<SoftwareReleaseItemResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_RELEASES.findIndex((r) => r.id === softwareReleaseId);
			if (idx !== -1) {
				MOCK_RELEASES[idx] = {
					...MOCK_RELEASES[idx],
					isPublished: body.isPublished,
					releasedAt: body.isPublished ? (body.releasedAt ?? new Date().toISOString()) : undefined,
				};
			}
			return of({ data: { ...MOCK_RELEASES[idx] } });
		}
		return this.http.patch<SoftwareReleaseItemResponseData | SoftwareReleaseItemResponseData['data']>(`${this.managementBase}/release/${softwareReleaseId}`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	uploadArtifact(softwareReleaseId: number, file: File): Observable<SoftwareUpdatesVoidResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_RELEASES.findIndex((r) => r.id === softwareReleaseId);
			if (idx !== -1) {
				MOCK_RELEASES[idx] = {
					...MOCK_RELEASES[idx],
					s3Filename: file.name,
					byteSize: file.size,
				};
			}
			return of({});
		}
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<SoftwareUpdatesVoidResponseData>(`${this.managementBase}/release/${softwareReleaseId}/artifact`, formData);
	}

	// --- Release notes ---
	getReleaseNotes(softwareReleaseId: number): Observable<ReleaseNoteListResponseData> {
		if (this.useMockData()) {
			return of({
				data: MOCK_RELEASE_NOTES.filter((note) => note.softwareReleaseId === softwareReleaseId),
			});
		}
		return this.http.get<ReleaseNoteListResponseData | ReleaseNoteListResponseData['data']>(`${this.managementBase}/release/${softwareReleaseId}/note`).pipe(
			map((body) => this.normalizeListResponse(body)),
		);
	}

	getReleaseNote(releaseNoteId: number): Observable<ReleaseNoteItemResponseData> {
		if (this.useMockData()) {
			const note = MOCK_RELEASE_NOTES.find((item) => item.id === releaseNoteId);
			return of(note ? { data: note } : {});
		}
		return this.http.get<ReleaseNoteItemResponseData | ReleaseNoteItemResponseData['data']>(`${this.managementBase}/release/note/${releaseNoteId}`).pipe(
			map((body) => this.normalizeItemResponse(body)),
		);
	}

	createOrUpdateReleaseNote(softwareReleaseId: number, body: CreateOrUpdateReleaseNoteRequest): Observable<ReleaseNoteItemResponseData> {
		if (this.useMockData()) {
			const existingIdx = MOCK_RELEASE_NOTES.findIndex(
				(note) => note.softwareReleaseId === softwareReleaseId && note.language === body.language,
			);
			if (existingIdx !== -1) {
				MOCK_RELEASE_NOTES[existingIdx] = { ...MOCK_RELEASE_NOTES[existingIdx], ...body };
				return of({ data: { ...MOCK_RELEASE_NOTES[existingIdx] } });
			}
			const nextId = Math.max(0, ...MOCK_RELEASE_NOTES.map((note) => note.id ?? 0)) + 1;
			const created: ReleaseNoteData = { ...body, id: nextId, softwareReleaseId };
			MOCK_RELEASE_NOTES.push(created);
			return of({ data: created });
		}
		return this.http.post<ReleaseNoteItemResponseData | ReleaseNoteItemResponseData['data']>(`${this.managementBase}/release/${softwareReleaseId}/note`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	updateReleaseNote(releaseNoteId: number, body: CreateOrUpdateReleaseNoteRequest): Observable<ReleaseNoteItemResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_RELEASE_NOTES.findIndex((note) => note.id === releaseNoteId);
			if (idx !== -1) {
				MOCK_RELEASE_NOTES[idx] = { ...MOCK_RELEASE_NOTES[idx], ...body, id: releaseNoteId };
			}
			return of({ data: { ...MOCK_RELEASE_NOTES[idx] } });
		}
		return this.http.put<ReleaseNoteItemResponseData | ReleaseNoteItemResponseData['data']>(`${this.managementBase}/release/note/${releaseNoteId}`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	deleteReleaseNote(releaseNoteId: number): Observable<SoftwareUpdatesVoidResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_RELEASE_NOTES.findIndex((note) => note.id === releaseNoteId);
			if (idx !== -1) {
				MOCK_RELEASE_NOTES.splice(idx, 1);
			}
			return of({});
		}
		return this.http.delete<SoftwareUpdatesVoidResponseData>(`${this.managementBase}/release/note/${releaseNoteId}`);
	}

	// --- Compatibility rules ---
	getCompatibilityRuleTypes(): Observable<CompatibilityRuleTypesData> {
		if (this.useMockData()) {
			return of(MOCK_COMPATIBILITY_RULE_TYPES);
		}
		return this.http.get<CompatibilityRuleTypesResponseData>(`${this.managementBase}/compatibility-rule/type`).pipe(
			map((body) => this.normalizeCompatibilityRuleTypesResponse(body)),
		);
	}

	getCompatibilityRules(softwareReleaseId: number): Observable<CompatibilityRuleListResponseData> {
		if (this.useMockData()) {
			return of({
				data: MOCK_COMPATIBILITY_RULES.filter((rule) => rule.softwareReleaseId === softwareReleaseId),
			});
		}
		return this.http.get<CompatibilityRuleListResponseData | CompatibilityRuleListResponseData['data']>(`${this.managementBase}/release/${softwareReleaseId}/compatibility-rule`).pipe(
			map((body) => this.normalizeListResponse(body)),
		);
	}

	createCompatibilityRule(softwareReleaseId: number, body: CreateCompatibilityRuleRequest): Observable<CompatibilityRuleItemResponseData> {
		if (this.useMockData()) {
			const nextId = Math.max(0, ...MOCK_COMPATIBILITY_RULES.map((rule) => rule.id ?? 0)) + 1;
			const created = { ...body, id: nextId, softwareReleaseId };
			MOCK_COMPATIBILITY_RULES.push(created);
			return of({ data: created });
		}
		return this.http.post<CompatibilityRuleItemResponseData | CompatibilityRuleItemResponseData['data']>(`${this.managementBase}/release/${softwareReleaseId}/compatibility-rule`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	getCompatibilityRule(compatibilityRuleId: number): Observable<CompatibilityRuleItemResponseData> {
		if (this.useMockData()) {
			const rule = MOCK_COMPATIBILITY_RULES.find((item) => item.id === compatibilityRuleId);
			return of(rule ? { data: rule } : {});
		}
		return this.http.get<CompatibilityRuleItemResponseData | CompatibilityRuleItemResponseData['data']>(`${this.managementBase}/release/compatibility-rule/${compatibilityRuleId}`).pipe(
			map((body) => this.normalizeItemResponse(body)),
		);
	}

	updateCompatibilityRule(compatibilityRuleId: number, body: UpdateCompatibilityRuleRequest): Observable<CompatibilityRuleItemResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_COMPATIBILITY_RULES.findIndex((rule) => rule.id === compatibilityRuleId);
			if (idx !== -1) {
				MOCK_COMPATIBILITY_RULES[idx] = { ...MOCK_COMPATIBILITY_RULES[idx], ...body, id: compatibilityRuleId };
			}
			return of({ data: MOCK_COMPATIBILITY_RULES[idx] ?? {} });
		}
		return this.http.put<CompatibilityRuleItemResponseData | CompatibilityRuleItemResponseData['data']>(`${this.managementBase}/release/compatibility-rule/${compatibilityRuleId}`, body).pipe(
			map((response) => this.normalizeItemResponse(response)),
		);
	}

	deleteCompatibilityRule(compatibilityRuleId: number): Observable<SoftwareUpdatesVoidResponseData> {
		if (this.useMockData()) {
			const idx = MOCK_COMPATIBILITY_RULES.findIndex((rule) => rule.id === compatibilityRuleId);
			if (idx !== -1) {
				MOCK_COMPATIBILITY_RULES.splice(idx, 1);
			}
			return of({});
		}
		return this.http.delete<SoftwareUpdatesVoidResponseData>(`${this.managementBase}/release/compatibility-rule/${compatibilityRuleId}`);
	}

	// --- Client (read-only, utile per preview) ---
	downloadRelease(softwareReleaseId: number): Observable<DownloadSoftwareReleaseItemResponseData> {
		if (this.useMockData()) {
			const release = MOCK_RELEASES.find((item) => item.id === softwareReleaseId);
			return of({
				data: {
					filename: release?.s3Filename ?? `release-${softwareReleaseId}.bin`,
					url: `https://mock.cameconnect.local/releases/${softwareReleaseId}/download`,
				},
			});
		}
		return this.http.get<DownloadSoftwareReleaseItemResponseData | DownloadSoftwareReleaseItemResponseData['data']>(
			`${this.clientBase}/release/${softwareReleaseId}/download`,
		).pipe(
			map((body) => this.normalizeItemResponse(body)),
		);
	}

	/** Normalizza body lista: accetta envelope API o array legacy. */
	private normalizeListResponse<T>(
		body: SoftwareUpdatesListResponseData<T> | T[] | null | undefined,
	): SoftwareUpdatesListResponseData<T> {
		if (Array.isArray(body)) {
			return { data: body };
		}
		return body ?? {};
	}

	/** Normalizza body tipi/operatori regole di compatibilità. */
	private normalizeCompatibilityRuleTypesResponse(
		body: CompatibilityRuleTypesResponseData | CompatibilityRuleTypesData['types'] | null | undefined,
	): CompatibilityRuleTypesData {
		if (body != null && typeof body === 'object' && 'data' in body) {
			const wrapped = (body as SoftwareUpdatesItemResponseData<CompatibilityRuleTypesData>).data;
			if (wrapped?.types || wrapped?.operators) {
				return wrapped;
			}
		}
		if (body != null && typeof body === 'object' && ('types' in body || 'operators' in body)) {
			return body as CompatibilityRuleTypesData;
		}
		if (Array.isArray(body)) {
			return { types: body, operators: [] };
		}
		return {};
	}

	/** Normalizza body singola entità: accetta envelope API o oggetto legacy. */
	private normalizeItemResponse<T>(
		body: SoftwareUpdatesItemResponseData<T> | T | null | undefined,
	): SoftwareUpdatesItemResponseData<T> {
		if (body != null && typeof body === 'object' && 'data' in body && !Array.isArray((body as SoftwareUpdatesListResponseData<T>).data)) {
			return body as SoftwareUpdatesItemResponseData<T>;
		}
		if (body != null && typeof body === 'object') {
			return { data: body as T };
		}
		return {};
	}
}
